import styled, { css } from 'styled-components'
import { MEDIA } from '~/common/styles/responsive'

export const guidePanelStyle = css`
	border: 1px solid #dfe3ec;
	border-radius: 10px;
	background: #f7f9ff;
	margin-bottom: 16px;
	overflow: hidden;
`

export const guideHeaderStyle = css`
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 12px 16px;
	cursor: pointer;
	user-select: none;

	.guide-header-icon {
		display: flex;
		align-items: center;
		color: ${({ theme }) => theme.palette.primary.main};

		svg {
			font-size: 20px;
		}
	}

	.guide-header-text {
		flex: 1;
		min-width: 0;

		h3 {
			margin: 0;
			font-size: 14px;
			font-weight: 600;
			color: #333335;
		}

		p {
			margin: 2px 0 0;
			font-size: 12px;
			color: #7e7e80;
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;

			/* No hover on a phone to reveal the rest: let the summary wrap. */
			${MEDIA.phone} {
				white-space: normal;
			}
		}
	}

	.guide-header-actions {
		display: flex;
		align-items: center;
		gap: 2px;
		flex-shrink: 0;
		font-size: 12px;
		font-weight: 600;
		/* Inside the V2 shell, V2's link blue (SSI-386); in Classic, the theme's. */
		color: ${({ theme }) => `var(--mv2-indigo, ${theme.palette.primary.main})`};

		svg {
			font-size: 18px;
		}
	}

	/* Hide / Show reads as a text link, the same way as V2's "View all" (SSI-448):
	   underlined under a mouse and on keyboard focus, never left underlined by a tap. */
	${MEDIA.hover} {
		&:hover .guide-header-actions {
			text-decoration: underline;
		}
	}

	&:focus-visible .guide-header-actions {
		text-decoration: underline;
	}
`

export const guideBodyStyle = css`
	padding: 0 16px 16px;
`

/**
 * The four steps, as one wrapping line instead of a 4-card grid — the grid's
 * full descriptions made the always-visible panel taller than the table it
 * was introducing. Each step keeps its title (still asserted by name in
 * role-guide.spec.js) plus an ⓘ carrying the description that used to sit in
 * the card body, through the same InfoTooltip every other "i" in the portal
 * uses.
 */
export const guideStepsCompactStyle = css`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 8px;
	margin-bottom: 4px;

	.step-chip {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		font-size: 12px;
		font-weight: 600;
		color: #333335;
		white-space: nowrap;

		${MEDIA.phone} {
			white-space: normal;
		}
	}

	.step-chip-number {
		flex-shrink: 0;
		width: 16px;
		height: 16px;
		border-radius: 50%;
		color: #ffffff;
		font-size: 10px;
		font-weight: 700;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.step-arrow {
		color: #c4c4c8;
		font-size: 13px;
	}
`

export const guideRoleGridStyle = css`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(min(230px, 100%), 1fr));
	gap: 12px;
	margin-top: 12px;
`

export const guideRoleCardStyle = css`
	background: #ffffff;
	border: 1px solid #e8ebf3;
	border-radius: 8px;
	padding: 12px;

	.role-chip {
		display: inline-block;
		background-color: #e6e6e6;
		padding: 3px 10px;
		color: #7e7e80;
		font-size: 11px;
		font-weight: 600;
		text-transform: uppercase;
		border-radius: 16px;
		margin-bottom: 8px;
	}

	.role-summary {
		margin: 0;
		font-size: 12px;
		line-height: 1.5;
		color: #5c5c60;
	}

	ul {
		margin: 8px 0 0;
		padding-left: 16px;

		li {
			font-size: 12px;
			line-height: 1.6;
			color: #5c5c60;
		}
	}
`

export const guideSubheadingStyle = css`
	margin: 16px 0 0;
	font-size: 13px;
	font-weight: 600;
	color: #333335;
`

export const guideIntroStyle = css`
	margin: 0 0 12px;
	font-size: 12px;
	line-height: 1.6;
	color: #5c5c60;
`

/**
 * The card behind a column-header ⓘ. Matches the workflow guide's tab card so
 * an "i" reads the same way wherever it appears in the portal.
 */
export const ColumnInfoCard = styled.div`
	padding: 12px 14px;
	max-width: 320px;

	.column-info-eyebrow {
		display: inline-block;
		font-size: 10px;
		font-weight: 700;
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: #8a8fa3;
		margin-bottom: 6px;
	}

	.column-info-title {
		margin: 0 0 6px;
		font-size: 13px;
		font-weight: 600;
		color: #1f2333;
	}

	.column-info-description {
		margin: 0;
		font-size: 12px;
		line-height: 1.55;
		color: #4a4f61;
	}

	.column-info-note {
		margin: 8px 0 0;
		padding-top: 8px;
		border-top: 1px solid #eceef5;
		font-size: 11px;
		line-height: 1.5;
		color: #8a8fa3;
	}
`

/** The inline hint under a field in the Assign role dialog. */
export const FieldHint = styled.p`
	margin: 6px 0 0;
	font-size: 11px;
	line-height: 1.5;
	color: #7e7e80;
`

/**
 * The same slot as FieldHint, in amber, for a consequence rather than an
 * explanation — a field left empty that will still submit, but not do what the
 * person filling the form probably expects.
 *
 * The colour is a literal for the same reason FieldHint's is: `status.warning.dark`
 * in the theme is `#DE62A`, five hex digits, and renders as nothing.
 */
export const FieldWarning = styled.p`
	margin: 6px 0 0;
	font-size: 11px;
	line-height: 1.5;
	color: #b26a00;
`
